import React, { useState, useRef, useEffect } from 'react';
import styles from './styles.module.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    VISITED_STATUS,
    removeCountryFromVisitedWithSync,
    updateVisitedMetaSync
} from '../reducer';
import { COUNTRY_NAMES } from '../../../constans/countries/countries';

export const VisitedListContainer = ({
    country,
    makeVisibleStyle,
    isShared = false
}) => {

    const countryCode = COUNTRY_NAMES.indexOf(country?.name);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const isMobile = window.innerWidth <= 768;

    const meta = useSelector((state) => {
        if (countryCode === -1) return null;
        return state.visitedList?.visitMeta?.[countryCode] || null;
    });

    const dispatch = useDispatch();

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [noteText, setNoteText] = useState(meta?.note || '');
    const [selectedYear, setSelectedYear] = useState(
        meta?.visitedYear || currentYear
    );

    const noteRef = useRef(null);
    const dateRef = useRef(null);

    useEffect(() => {
        const monthSelect = document.getElementById(
            `month-${countryCode}`
        );
        if (!monthSelect) return;
        if (
            selectedYear === currentYear &&
            Number(monthSelect.value) > currentMonth
        ) {
            monthSelect.value = currentMonth;
        }
    }, [selectedYear, currentYear, currentMonth, countryCode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isEditingNote &&
                noteRef.current &&
                !noteRef.current.contains(event.target)
            ) {
                cancelNote();
            }
            if (
                showDatePicker &&
                dateRef.current &&
                !dateRef.current.contains(event.target)
            ) {
                setShowDatePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditingNote, showDatePicker]);

    useEffect(() => {
        if (!isEditingNote) {
            setNoteText(meta?.note || '');
        }
    }, [meta?.note, isEditingNote]);

    if (!country || !country.name || countryCode === -1) {
        return null;
    }

    const hasDate =
        meta?.visitedYear &&
        meta?.visitedMonth;

    const hasNote = Boolean(meta?.note?.trim());

    const cancelNote = () => {
        setNoteText(meta?.note || '');
        setIsEditingNote(false);
    };

    const saveNote = () => {
        const trimmedNote = noteText.trim();
        dispatch(updateVisitedMetaSync(countryCode, {
            ...meta,
            note: trimmedNote || null,
        }));
        setNoteText(trimmedNote);
        setIsEditingNote(false);
    };

    const startEditing = () => {
        setNoteText(meta?.note || '');
        setIsEditingNote(true);
    };

    const updateMeta = (newStatus) => {
        if (meta?.status === newStatus) {
            dispatch(updateVisitedMetaSync(countryCode, {
                status: null
            }));
            return;
        }
        dispatch(updateVisitedMetaSync(countryCode, {
            status: newStatus
        }));
    };

    const updateLastVisitDate = (month, year) => {
        dispatch(updateVisitedMetaSync(countryCode, {
            visitedMonth: Number(month),
            visitedYear: Number(year)
        }));
        setShowDatePicker(false);
    };

    const formatDate = () => {
        const monthNames = [
            'JAN', 'FEB', 'MAR',
            'APR', 'MAY', 'JUN',
            'JUL', 'AUG', 'SEP',
            'OCT', 'NOV', 'DEC'
        ];
        if (!hasDate) return '';
        return `${monthNames[meta.visitedMonth - 1]} ${meta.visitedYear}`;
    };

    
    const years = [];
    for (let i = currentYear; i >= currentYear - 100; i--) {
        years.push(i);
    }
    const months = [
        'JAN', 'FEB', 'MAR',
        'APR', 'MAY', 'JUN',
        'JUL', 'AUG', 'SEP',
        'OCT', 'NOV', 'DEC'
    ];

    const availableMonths =
    selectedYear === currentYear
        ? months.slice(0, currentMonth)
        : months;

    const getStatusLabel = () => {
        switch (meta?.status) {
            case VISITED_STATUS.LOVED:
                return (
                    <div className={styles.status_loved_label}>
                        ♥︎ Loved
                    </div>
                );
            case VISITED_STATUS.AGAIN:
                return (
                    <div className={styles.status_again_label}>
                        ⟳ Again
                    </div>
                );
            case VISITED_STATUS.ONCE:
                return (
                    <div className={styles.status_once_label}>
                        ✓ Once
                    </div>
                );
            case VISITED_STATUS.HIDDEN_GEM:
                return (
                    <div className={styles.status_gem_label}>
                        ✦ Hidden Gem
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.visitedContainer}>
                <div className={styles.meta_labels}>
                    {hasDate && (
                        <span className={styles.date_label}>
                            {formatDate()}
                        </span>
                    )}
                </div>
                <div className={styles.center_container}>
                    <div className={styles.flag}>{country.flag}</div>
                    <div className={styles.name}>{country.name}</div>
                </div>

                {getStatusLabel()}
                
                {!isShared && (
                    <div
                        className={styles[makeVisibleStyle]}
                        onClick={() =>
                            dispatch(
                                removeCountryFromVisitedWithSync(countryCode)
                            )
                        }
                    >
                        {'\u232b'}
                    </div>
                )}
            </div>

            {!isShared && (
                <div className={styles.button_row}>
                    <span
                        className={styles.action_pill}
                        onClick={startEditing}
                    >
                        ✏️ {hasNote ? 'Edit note' : 'Add note'}
                    </span>
                    <div
                        className={styles.date_picker_wrapper}
                        ref={dateRef}
                    >
                        <span
                            className={styles.action_pill}
                            onClick={() =>
                                setShowDatePicker(!showDatePicker)
                            }
                        >
                            🗓️ {hasDate ? 'Edit date' : 'Add date'}
                        </span>

                        {showDatePicker && (
                            <div className={styles.custom_date_picker}>
                                <select
                                    defaultValue={
                                        meta?.visitedMonth || new Date().getMonth() + 1
                                    }
                                    id={`month-${countryCode}`}
                                >
                                    {availableMonths.map((month) => {
                                        const monthIndex = months.indexOf(month) + 1;

                                        return (
                                            <option
                                                key={month}
                                                value={monthIndex}
                                            >
                                                {month}
                                            </option>
                                        );
                                    })}
                                </select>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    id={`year-${countryCode}`}
                                >
                                    {years.map((year) => (
                                        <option
                                            key={year}
                                            value={year}
                                        >
                                            {year}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className={styles.date_save_btn}
                                    onClick={() => {
                                        const month =
                                            document.getElementById(
                                                `month-${countryCode}`
                                            ).value;
                                        const year =
                                            document.getElementById(
                                                `year-${countryCode}`
                                            ).value;
                                        updateLastVisitDate(month, year);
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div ref={noteRef} className={styles.container}>
                <div
                    className={`
                        ${styles.note_container}
                        ${hasNote ? styles.has_note : ''}
                        ${isEditingNote ? styles.editing : ''}
                    `}
                >
                    {isEditingNote ? (
                        <div className={styles.note_input_wrapper}>
                            <textarea
                                className={styles.note_input}
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value.slice(0, 150))}
                                rows={1}
                                maxLength={150}
                                placeholder="Add your note about this country..."
                                autoFocus
                            />
                            <span className={styles.note_counter}>
                                {noteText.trim().length}/150
                            </span>
                            <div className={styles.note_buttons}>
                                <button
                                    className={styles.note_save_btn}
                                    onClick={saveNote}
                                >
                                    Save
                                </button>
                                <button
                                    className={styles.note_cancel_btn}
                                    onClick={cancelNote}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                    ) : (
                        hasNote && (
                            <div className={styles.note_display}>
                                <span className={styles.note_text}>
                                    {meta.note}
                                </span>
                            </div>
                        )
                    )}
                </div>
            </div>

            {!isShared && (
                <div className={styles.status_selection}>
                    <span
                        className={`
                            ${styles.status_loved}
                            ${meta?.status === VISITED_STATUS.LOVED
                                ? styles.active
                                : ''}
                        `}
                        onClick={() =>
                            updateMeta(VISITED_STATUS.LOVED)
                        }
                    >
                        {isMobile ? (
                            <>
                                <span>♥︎</span>
                                <span>Loved</span>
                            </>
                            ) : ('♥︎ Loved')
                        }
                    </span>

                    <span
                        className={`
                            ${styles.status_again}
                            ${meta?.status === VISITED_STATUS.AGAIN
                                ? styles.active
                                : ''}
                        `}
                        onClick={() =>
                            updateMeta(VISITED_STATUS.AGAIN)
                        }
                    >   {isMobile ? (
                            <>
                                <span>⟳</span>
                                <span>Again</span>
                            </>
                            ) : ('⟳ Again')
                        }
                    </span>

                    <span
                        className={`
                            ${styles.status_once}
                            ${meta?.status === VISITED_STATUS.ONCE
                                ? styles.active
                                : ''}
                        `}
                        onClick={() =>
                            updateMeta(VISITED_STATUS.ONCE)
                        }
                    >   {isMobile ? (
                            <>
                                <span>✓</span>
                                <span>Once</span>
                            </>
                            ) : ('✓ Once')
                        }
                    </span>

                    <span
                        className={`
                            ${styles.status_gem}
                            ${meta?.status === VISITED_STATUS.HIDDEN_GEM
                                ? styles.active
                                : ''}
                        `}
                        onClick={() =>
                            updateMeta(VISITED_STATUS.HIDDEN_GEM)
                        }
                    >   {isMobile ? (
                            <>
                                <span>✦</span>
                                <span>Hidden Gem</span>
                            </>
                            ) : ('✦ Hidden Gem')
                        }
                    </span>

                </div>
            )}
        </div>
    );
};